import HiddenInput from "@/components/form/components/HiddenInput";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationStandardProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDate, formatLocaleNettoPoints } from "@/utils/formatUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Col, Divider, Form, Input, Modal, Row, Space, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import {
  useDownloadCalculatedCommissionsExportForAgentOutputs,
  useFilterCalculatedCommissionsForAgentOutputs,
} from "../../../../../common/api/queries";
import { ModalSizes, PageSizes, rowGutter } from "../../../../../common/constants";
import { ExportFileType } from "../../../../../common/enums";
import { UUID } from "../../../../../typings/global";
import { CONTRACT_ROUTE_PATHS } from "../../../../contract/paths";
import InstitutionSelect from "../../../../enumerations/components/form/InstitutionSelect";
import { CommissionKindTag } from "../../../batch/components/CommissionKindTag";
import { CommissionKind } from "../../../batch/enums";
import { removeBatchNameCustomSuffix } from "../../../batch/utils";
import { CalculatedCommissionAgentProfile, CalculatedCommissionsFilterPageRequest } from "../../types";

interface Props {
  onClose: VoidFunction;
  agentId: UUID;
  commissionId?: UUID;
  isOpen: boolean;
}

const colSpan = 8;

export const OutputCalculatedCommissionModal = ({ onClose, isOpen, agentId, commissionId }: Props) => {
  const { t } = useTranslation();

  const [filterData, setFilterData] = useState<CalculatedCommissionsFilterPageRequest>({ pageSize: PageSizes.SMALL });

  const [form] = Form.useForm<CalculatedCommissionsFilterPageRequest>();

  const { data, isFetching } = useFilterCalculatedCommissionsForAgentOutputs({
    id1: agentId,
    id2: commissionId ?? "",
    object: filterData,
  });

  const downloadOutputCalculatedCommissionsExport = useDownloadCalculatedCommissionsExportForAgentOutputs();

  const columns: ColumnsType<CalculatedCommissionAgentProfile> = [
    {
      key: "sequenceNumber",
      title: "#",
      width: 40,
      render: (_, __, index) => numberOrZero(data?.pageIndex) * numberOrZero(data?.pageSize) + index + 1,
    },
    {
      key: "contract",
      title: t("commissions.batch.attrs.commission.contract"),
      width: 90,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <>
          {record.commission.contractNumber && (
            <>
              <Ellipsis tooltip={record.commission.contractNumber}>
                {record.commission.commissionKind != CommissionKind.BONUS &&
                record.commission.commissionKind != CommissionKind.BONUS_EXTENDED &&
                record.commission.contract ? (
                  <Link
                    to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.commission.contract.id })}
                    target="_blank"
                  >
                    {record.commission.contractNumber}
                  </Link>
                ) : (
                  record.commission.contractNumber
                )}
              </Ellipsis>
              <br />
            </>
          )}
          {record.commission.secondaryContractNumber && (
            <Ellipsis tooltip={record.commission.secondaryContractNumber}>
              {record.commission.commissionKind != CommissionKind.BONUS &&
              record.commission.commissionKind != CommissionKind.BONUS_EXTENDED &&
              record.commission.contract ? (
                <Link
                  to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.commission.contract.id })}
                  target="_blank"
                >
                  {record.commission.secondaryContractNumber}
                </Link>
              ) : (
                record.commission.secondaryContractNumber
              )}
            </Ellipsis>
          )}
        </>
      ),
    },
    {
      key: "clientName",
      title: t("commissions.batch.attrs.commission.clientName"),
      width: 90,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.commission.clientName}</Ellipsis>,
    },
    {
      key: "institution",
      title: t("commissions.batch.attrs.commission.institution"),
      width: 90,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.commission.institution.name}</Ellipsis>,
    },
    {
      key: "product",
      title: t("commissions.batch.attrs.commission.product"),
      width: 90,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.commission.contract?.product.name}</Ellipsis>,
    },
    {
      key: "dates",
      title: t("commissions.batch.attrs.commission.dates"),
      width: 95,
      render: (_, record) => (
        <>
          {record.commission.startDate ? formatLocaleDate(record.commission.startDate) : undefined}
          <br />
          {record.commission.endDate ? formatLocaleDate(record.commission.endDate) : undefined}
        </>
      ),
    },
    {
      key: "commissionKind",
      title: t("commissions.batch.enums.commissionKind._label"),
      width: 190,
      align: "center",
      render: (_, record) => <CommissionKindTag kind={record.commission.commissionKind || CommissionKind.INITIAL} />,
    },
    {
      key: "nettoPointAmount",
      title: t("commissions.batch.attrs.commission.calculatedCommissions.nettoPointAmount"),
      align: "right",
      width: 85,
      render: (_, record) =>
        isDefined(record.nettoPointAmount) ? formatLocaleNettoPoints(record.nettoPointAmount) : undefined,
    },
    {
      key: "commissionBaseAmount",
      title: t("commissions.batch.attrs.commission.commissionBaseAmount"),
      align: "right",
      width: 90,
      render: (_, record) =>
        record.commission.commissionKind != CommissionKind.BONUS &&
        record.commission.commissionKind != CommissionKind.BONUS_EXTENDED &&
        isDefined(record.commission.commissionBaseAmount)
          ? formatLocaleCurrency(record.commission.commissionBaseAmount)
          : undefined,
    },
    {
      key: "commissionAmount",
      title: t("commissions.batch.attrs.commission.calculatedCommissions.commissionAmount"),
      align: "right",
      width: 90,
      render: (_, record) =>
        isDefined(record.commissionAmount) ? formatLocaleCurrency(record.commissionAmount) : undefined,
    },
  ];

  return (
    <Modal
      open={isOpen}
      width={ModalSizes.XXL}
      title={
        data?.batch
          ? `${t("user.titles.outputCalculatedCommissions")} ${removeBatchNameCustomSuffix(data.batch.name)}`
          : t("user.titles.outputCalculatedCommissions")
      }
      footer={
        <Button onClick={onClose} icon={<AntIcon type="close" />}>
          {t("common.close")}
        </Button>
      }
      onCancel={onClose}
      afterClose={() => form.resetFields()}
      mask={{ closable: false }}
    >
      <Card className="margin-bottom-medium margin-top-small">
        <Form
          form={form}
          layout="vertical"
          onFinish={(data) => setFilterData({ ...data, pageIndex: 0 })}
          name="outputCalculatedCommissionsFilterForm"
        >
          <HiddenInput name="pageSize" initialValue={PageSizes.SMALL.toString()} />

          <Row gutter={rowGutter} justify="center">
            <Col span={colSpan}>
              <Form.Item
                name="keyword"
                label={t("common.searchKeyword")}
                rules={[
                  validations.size(
                    validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                    validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
                  ),
                ]}
              >
                <Input allowClear placeholder={t("commissions.batch.helpers.commissionsSearchHint")} />
              </Form.Item>
            </Col>

            <Col span={colSpan}>
              <InstitutionSelect
                formItemProps={{
                  name: "institutionIds",
                  label: t("common.institutions"),
                  rules: [validations.none],
                }}
                selectProps={{ mode: "multiple", maxTagCount: "responsive", allowClear: true }}
                optionsProps={{ groupByType: true }}
              />
            </Col>

            <Col flex="110px">
              <Form.Item className="form-item-without-label">
                <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
                  {t("common.filter")}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Row gutter={rowGutter}>
        <Col span={24}>
          <div className={data?.totalElementsCount ? "table-header-margin" : "margin-bottom-small"}>
            <Space size="large">
              <div>
                <ActionTextIcon
                  icon="download"
                  color="blue"
                  text={t("common.exportXlsx")}
                  onClick={() =>
                    downloadOutputCalculatedCommissionsExport.mutate({
                      id1: agentId,
                      id2: commissionId ?? "",
                      object: {
                        ...filterData,
                        exportFileType: ExportFileType.XLSX,
                      },
                    })
                  }
                />

                <Divider orientation="vertical" />

                <ActionTextIcon
                  icon="download"
                  color="green"
                  text={t("common.exportCsv")}
                  onClick={() =>
                    downloadOutputCalculatedCommissionsExport.mutate({
                      id1: agentId,
                      id2: commissionId ?? "",
                      object: {
                        ...filterData,
                        exportFileType: ExportFileType.CSV,
                      },
                    })
                  }
                />
              </div>

              <div>
                <b>{t("commissions.special.helpers.filteredCommissionAmountsSum")}: </b>
                {data?.commissionAmountsSum ? formatLocaleCurrency(data.commissionAmountsSum) : undefined}
              </div>
            </Space>
          </div>

          <Table<CalculatedCommissionAgentProfile>
            {...tableStandardProps()}
            columns={columns}
            scroll={{ x: 950 }}
            dataSource={data?.pageData}
            pagination={{
              ...paginationStandardProps,
              placement: ["topEnd"],
              current: numberOrZero(data?.pageIndex) + 1,
              pageSize: data?.pageSize,
              total: data?.totalElementsCount,
              onChange: (pageNumber) => setFilterData({ ...filterData, pageIndex: pageNumber - 1 }),
            }}
            loading={isFetching}
          />
        </Col>
      </Row>
    </Modal>
  );
};
